import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import VideoPlayer from './VideoPlayer';

const mergeSegments = (segments) => {
    if (segments.length < 2) return segments;
    const sorted = [...segments].sort((a, b) => a.start - b.start);
    const merged = [{ ...sorted[0] }];
    for (let i = 1; i < sorted.length; i++) {
        const last = merged[merged.length - 1];
        if (sorted[i].start <= last.end) {
            last.end = Math.max(last.end, sorted[i].end);
        } else {
            merged.push({ ...sorted[i] });
        }
    }
    return merged;
};

// Max gap (seconds) between two timeupdate ticks that still counts as continuous playback.
// A seek (forward jump or rewind) produces a larger gap and is therefore not credited.
const MAX_PLAYBACK_GAP = 1.5;

const ShareKnowledgeVideoPlayer = ({ onHeartbeat, onEndVideo, lastTimeViewed, ...rest }) => {
    const watchedSegments = useRef([]);
    const segmentStart = useRef(null);
    const lastKnownTime = useRef(0);
    // Furthest point reached by continuous playback. Advances only while watching at the frontier
    // (see timeupdate), so a forward jump is never credited. Starts at the resumed position.
    const watchedFrontier = useRef(lastTimeViewed || 0);
    // Actual current playhead, updated on playback and on every seek (incl. rewind).
    const lastPlayhead = useRef(lastTimeViewed || 0);
    const heartbeatTimer = useRef(null);
    const hasEnded = useRef(false);

    // Resume point = min(frontier, playhead): a forward seek is capped by the frontier (not credited),
    // while a rewind is honored (resume from where the user left off).
    const getResumeTime = () => Math.min(watchedFrontier.current, lastPlayhead.current);

    const _startSegment = () => {
        if (segmentStart.current === null) {
            segmentStart.current = lastKnownTime.current;
        }
    };

    const _endSegment = () => {
        if (segmentStart.current !== null) {
            const end = lastKnownTime.current;
            if (end > segmentStart.current) {
                watchedSegments.current = mergeSegments(
                    [...watchedSegments.current, { start: segmentStart.current, end }]
                );
            }
            segmentStart.current = null;
        }
    };

    const handleInitPlayer = (player) => {
        // Resume on open (sole seek source — lastTimeViewed is intentionally not passed to the base
        // player). Seek on loadedmetadata so it sticks for HLS (duration not ready at mount).
        if (lastTimeViewed > 0) {
            player.one('loadedmetadata', () => {
                player.currentTime(lastTimeViewed);
            });
        }

        player.on('timeupdate', () => {
            if (!player.paused()) {
                const currentTime = player.currentTime();
                // Advance the frontier only on continuous forward playback. A forward jump
                // (currentTime far ahead) or a rewind (currentTime behind) is ignored.
                const advance = currentTime - watchedFrontier.current;
                if (advance > 0 && advance <= MAX_PLAYBACK_GAP) {
                    watchedFrontier.current = currentTime;
                }
                lastKnownTime.current = currentTime;
                lastPlayhead.current = currentTime;
            }
        });

        player.on('seeking', () => {
            _endSegment();
        });

        player.on('seeked', () => {
            // The playhead follows every seek (incl. rewind) — capped later by the frontier so a
            // forward jump is never credited, while a rewind is honored as the resume point.
            lastPlayhead.current = player.currentTime();
            if (!player.paused()) {
                lastKnownTime.current = player.currentTime();
                _startSegment();
            }
        });

        player.on('play', () => {
            lastKnownTime.current = player.currentTime();
            _startSegment();

            if (!heartbeatTimer.current && onHeartbeat) {
                heartbeatTimer.current = setInterval(() => {
                    const live = segmentStart.current !== null && lastKnownTime.current > segmentStart.current
                        ? { start: segmentStart.current, end: lastKnownTime.current }
                        : null;
                    const snapshot = live
                        ? mergeSegments([...watchedSegments.current, live])
                        : watchedSegments.current;
                    if (snapshot.length > 0) {
                        onHeartbeat(snapshot, getResumeTime());
                    }
                }, 30000);
            }
        });

        player.on('pause', () => {
            _endSegment();
            if (heartbeatTimer.current) {
                clearInterval(heartbeatTimer.current);
                heartbeatTimer.current = null;
            }
        });
    };

    const handleEndVideo = () => {
        hasEnded.current = true;
        _endSegment();
        if (heartbeatTimer.current) {
            clearInterval(heartbeatTimer.current);
            heartbeatTimer.current = null;
        }
        onEndVideo && onEndVideo(watchedSegments.current, getResumeTime());
    };

    useEffect(() => {
        return () => {
            if (heartbeatTimer.current) clearInterval(heartbeatTimer.current);
            if (!hasEnded.current) {
                _endSegment();
                if (watchedSegments.current.length > 0) {
                    onHeartbeat && onHeartbeat(watchedSegments.current, getResumeTime());
                }
            }
        };
    }, []);

    return (
        <VideoPlayer
            {...rest}
            onInitVideoPlayer={handleInitPlayer}
            onEndVideo={handleEndVideo}
        />
    );
};

ShareKnowledgeVideoPlayer.propTypes = {
    onHeartbeat: PropTypes.func,
    onEndVideo: PropTypes.func,
    lastTimeViewed: PropTypes.number
};

export default ShareKnowledgeVideoPlayer;
